import { z } from 'zod';

/**
 * Create Aggregate Request DTO
 * 
 * Validates client input for aggregate creation
 */
export const CreateAggregateRequestSchema = z.object({
    name: z.string()
        .min(2, 'Aggregate name must be at least 2 characters')
        .max(100, 'Aggregate name must be less than 100 characters')
        .regex(/^[a-zA-Z][a-zA-Z0-9]*$/, 'Aggregate name must be PascalCase (e.g., OrderManagement)'),
    description: z.string()
        .min(10, 'Description must be at least 10 characters')
        .max(1000, 'Description must be less than 1000 characters'),
    purpose: z.string()
        .min(20, 'Purpose must be at least 20 characters')
        .max(500, 'Purpose must be less than 500 characters'),
    boundedContextId: z.string().uuid('Invalid bounded context ID format'),
    structure: z.object({
        rootEntity: z.object({
            name: z.string().min(1).max(100),
            description: z.string().min(5).max(500)
        }),
        entities: z.array(z.object({
            name: z.string().min(1).max(100),
            description: z.string().min(5).max(500),
            keyAttributes: z.array(z.string().min(1).max(100)).max(20)
        })).max(10).optional(),
        valueObjects: z.array(z.object({
            name: z.string().min(1).max(100),
            description: z.string().min(5).max(500),
            properties: z.array(z.string().min(1).max(100)).max(20)
        })).max(20).optional(),
        domainServices: z.array(z.object({
            name: z.string().min(1).max(100),
            description: z.string().min(5).max(500),
            operations: z.array(z.string().min(1).max(100)).max(10)
        })).max(5).optional()
    }),
    behavior: z.object({
        commands: z.array(z.object({
            name: z.string().min(1).max(100),
            description: z.string().min(5).max(500),
            parameters: z.array(z.string().min(1).max(100)).max(10)
        })).max(20).optional(),
        events: z.array(z.object({
            name: z.string().min(1).max(100),
            description: z.string().min(5).max(500),
            properties: z.array(z.string().min(1).max(100)).max(10)
        })).max(20).optional(),
        queries: z.array(z.object({
            name: z.string().min(1).max(100),
            description: z.string().min(5).max(500),
            returnType: z.string().min(1).max(100)
        })).max(10).optional()
    }).optional(),
    invariants: z.array(z.object({
        name: z.string().min(1).max(100),
        description: z.string().min(5).max(500),
        rule: z.string().min(10).max(1000)
    })).max(10).optional()
});

export type CreateAggregateRequest = z.infer<typeof CreateAggregateRequestSchema>;

/**
 * Update Aggregate Request DTO
 */
export const UpdateAggregateRequestSchema = z.object({
    name: z.string()
        .min(2, 'Aggregate name must be at least 2 characters')
        .max(100, 'Aggregate name must be less than 100 characters')
        .regex(/^[a-zA-Z][a-zA-Z0-9]*$/, 'Aggregate name must be PascalCase')
        .optional(),
    description: z.string()
        .min(10, 'Description must be at least 10 characters')
        .max(1000, 'Description must be less than 1000 characters')
        .optional(),
    purpose: z.string()
        .min(20, 'Purpose must be at least 20 characters')
        .max(500, 'Purpose must be less than 500 characters')
        .optional(),
    status: z.enum(['designed', 'implementing', 'implemented', 'refactoring']).optional(),
    structure: z.object({
        rootEntity: z.object({
            name: z.string().min(1).max(100),
            description: z.string().min(5).max(500)
        }),
        entities: z.array(z.object({
            name: z.string().min(1).max(100),
            description: z.string().min(5).max(500),
            keyAttributes: z.array(z.string().min(1).max(100)).max(20)
        })).max(10).optional(),
        valueObjects: z.array(z.object({
            name: z.string().min(1).max(100),
            description: z.string().min(5).max(500),
            properties: z.array(z.string().min(1).max(100)).max(20)
        })).max(20).optional(),
        domainServices: z.array(z.object({
            name: z.string().min(1).max(100),
            description: z.string().min(5).max(500),
            operations: z.array(z.string().min(1).max(100)).max(10)
        })).max(5).optional()
    }).optional(),
    behavior: z.object({
        commands: z.array(z.object({
            name: z.string().min(1).max(100),
            description: z.string().min(5).max(500),
            parameters: z.array(z.string().min(1).max(100)).max(10)
        })).max(20).optional(),
        events: z.array(z.object({
            name: z.string().min(1).max(100),
            description: z.string().min(5).max(500),
            properties: z.array(z.string().min(1).max(100)).max(10)
        })).max(20).optional(),
        queries: z.array(z.object({
            name: z.string().min(1).max(100),
            description: z.string().min(5).max(500),
            returnType: z.string().min(1).max(100)
        })).max(10).optional()
    }).optional(),
    invariants: z.array(z.object({
        name: z.string().min(1).max(100),
        description: z.string().min(5).max(500),
        rule: z.string().min(10).max(1000)
    })).max(10).optional()
});

export type UpdateAggregateRequest = z.infer<typeof UpdateAggregateRequestSchema>;

/**
 * Get Aggregates Query Parameters DTO
 */
export const GetAggregatesQuerySchema = z.object({
    boundedContextId: z.string().uuid().optional(),
    domainId: z.string().uuid().optional(),
    status: z.enum(['designed', 'implementing', 'implemented', 'refactoring', 'all']).default('implemented'),
    complexity: z.enum(['simple', 'moderate', 'complex', 'very_complex', 'all']).default('all'),
    limit: z.coerce.number().min(1).max(100).default(20),
    offset: z.coerce.number().min(0).default(0),
    search: z.string().max(100).optional(),
    includeStructure: z.coerce.boolean().default(false),
    includeBehavior: z.coerce.boolean().default(false)
});

export type GetAggregatesQuery = z.infer<typeof GetAggregatesQuerySchema>;

/**
 * Aggregate Parameters DTO
 */
export const AggregateParamsSchema = z.object({
    aggregateId: z.string().uuid('Invalid aggregate ID format')
});

export type AggregateParams = z.infer<typeof AggregateParamsSchema>;